/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  SUBJECT_ORG_ONBOARDING,
  TUTORIAL_NAME_POST,
  TUTORIAL_NAME_SEARCH,
  TUTORIAL_NAME_TOPIC,
} from '../constants/onboarding';
import { persistReducer } from '../utils/persistRedux';

export type TutorialName =
  | typeof TUTORIAL_NAME_TOPIC
  | typeof TUTORIAL_NAME_POST
  | typeof TUTORIAL_NAME_SEARCH;

export type OnboardingSubject = typeof SUBJECT_ORG_ONBOARDING | TutorialName;

export interface Onboarding {
  subject: OnboardingSubject;
  step: number;
  completedAt: string | null;
  skippedAt: string | null;
}

type OnboardingActiveBeacons = {
  [id: string]: boolean | undefined;
};

type OnboardingState = {
  activeBeacons: OnboardingActiveBeacons;
  activeTutorial: TutorialName | null;
  data: {
    [SUBJECT_ORG_ONBOARDING]?: Onboarding;
    [TUTORIAL_NAME_TOPIC]?: Onboarding;
    [TUTORIAL_NAME_POST]?: Onboarding;
    [TUTORIAL_NAME_SEARCH]?: Onboarding;
  };
};

const initialState: OnboardingState = {
  activeBeacons: {},
  activeTutorial: null,
  data: {},
};

const onboardingSlice = createSlice({
  name: 'onboardingSlice',
  initialState,
  reducers: {
    setActiveTutorial(state, action: PayloadAction<TutorialName | null>) {
      state.activeTutorial = action.payload;
    },
    setActiveBeacons(state, action: PayloadAction<string[] | undefined>) {
      state.activeBeacons = Object.fromEntries(
        action.payload?.map((id) => [id, true]) ?? [],
      );
    },
    setOnboardingData(
      state,
      action: PayloadAction<{ subject: OnboardingSubject; step?: number }>,
    ) {
      state.data = { ...state.data, [action.payload.subject]: action.payload };
    },
  },
});

export const OnboardingActions = onboardingSlice.actions;

export default persistReducer(
  { key: 'onboarding', whitelist: ['data'] },
  onboardingSlice.reducer,
);
